import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './MyListingsPage.module.css';

function formatPrice(price, type) {
  const unit = type === 'wynajem' ? ' /mies.' : '';
  if (price >= 1000000) return (price / 1000000).toFixed(2).replace('.', ',') + ' mln zł' + unit;
  return Number(price).toLocaleString('pl-PL') + ' zł' + unit;
}

const BADGE = { sprzedaz: 'Sprzedaż', wynajem: 'Wynajem', nowe: 'Nowe' };
function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const d = Math.ceil((new Date(expiresAt) - Date.now()) / 86400000);
  if (d < 0) return null;
  return d;
}

const BG = ['#E8D5A8','#B5D4F4','#C0DD97','#F4C0D1','#9FE1CB','#FAC775'];

export default function MyListingsPage() {
  const { user, token, isLoggedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) { navigate('/logowanie'); return; }
    fetch('/api/listings/my', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setListings(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => { setError('Błąd pobierania danych'); setLoading(false); });
  }, [isLoggedIn, authLoading, token, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to ogłoszenie?')) return;
    setDeleting(id);
    try {
      const res = await fetch(`/api/listings/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setListings(l => l.filter(x => x.id !== id));
      else setError('Błąd usuwania ogłoszenia');
    } catch { setError('Błąd połączenia'); }
    setDeleting(null);
  };

  if (authLoading || loading) return <div className={styles.status}>Ładowanie...</div>;
  if (error) return <div className={styles.statusErr}>{error}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Moje ogłoszenia</h1>
          <p>Zalogowany jako <strong>{user?.name}</strong> · {user?.email}</p>
        </div>
        <Link to="/dodaj" className={styles.addBtn}>+ Dodaj ogłoszenie</Link>
      </div>

      {listings.length === 0 ? (
        <div className={styles.empty}>
          <p>Nie masz jeszcze żadnych ogłoszeń.</p>
          <Link to="/dodaj" className={styles.addBtn}>Dodaj pierwsze ogłoszenie</Link>
        </div>
      ) : (
        <div className={styles.grid}>
          {listings.map((l, i) => (
            <div key={l.id} className={styles.card}>
              {l.status === 'approved' && l.expiresAt && daysLeft(l.expiresAt) !== null && daysLeft(l.expiresAt) <= 7 && (
                <div className={styles.statusExpiring}>
                  ⏳ Wygasa za {daysLeft(l.expiresAt)} dni
                </div>
              )}
              {l.status === 'expired' && (
                <div className={styles.statusExpired}>
                  💤 Ogłoszenie wygasło
                </div>
              )}
              {l.status !== 'approved' && l.status !== 'expired' && (
                <div className={styles[l.status === 'pending' ? 'statusPending' : 'statusRejected']}>
                  {l.status === 'pending' ? '⏳ Oczekuje na akceptację' : '✗ Odrzucone'}
                  {l.status === 'rejected' && l.rejectionReason && (
                    <span className={styles.rejectionReason}> — {l.rejectionReason}</span>
                  )}
                </div>
              )}
              <div className={styles.cardImg} style={{ background: `linear-gradient(135deg, ${BG[i % BG.length]}cc, ${BG[i % BG.length]})`, position:'relative', overflow:'hidden' }}>
                {l.images?.[0]?.url
                  ? <img src={l.images[0].url} alt={l.title} style={{width:'100%',height:'100%',objectFit:'cover',position:'absolute',top:0,left:0}} />
                  : <span className={styles.emoji}>🏠</span>
                }
                <span className={`${styles.badge} ${styles['badge_' + l.type]}`}>{BADGE[l.type]}</span>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.price}>{formatPrice(l.price, l.type)}</div>
                <div className={styles.title}>{l.title}</div>
                <div className={styles.location}>◎ {l.location}</div>
                <div className={styles.meta}>
                  <span>{l.area} m²</span>
                  <span>{l.rooms} pok.</span>
                  <span>{l.floor}</span>
                </div>
                <div className={styles.actions}>
                  <Link to={`/ogloszenia/${l.slug || l.id}`} className={styles.viewBtn}>Podgląd</Link>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(l.id)}
                    disabled={deleting === l.id}
                  >
                    {deleting === l.id ? 'Usuwanie...' : 'Usuń'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
